import React, { useEffect, useMemo, useRef, useState } from "react";
import { Archive, ArchiveRestore, CalendarDays, Phone, MessageSquare, User, ChevronLeft, ChevronRight, Mic, Play, Voicemail } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  useGetCallDispositionsQuery,
  useGetCallQuery,
  useGetCallsQuery,
  useGetCallStatsQuery,
  useGetOrganisationsQuery,
  useRestoreCallMutation,
} from "@/store/rest_api";
import { cn } from "@/lib/utils";
import { useCallDialog } from "@/context/CallDialogContext";
import { useMessageDialog } from "@/context/MessageDialogContext";
import { CallFlowStepsList } from "@/components/pages/calls/CallFlowStepsList";
import CallActionsMenu from "@/components/calls/CallActionsMenu";
import CallExpandedPanel from "@/components/calls/CallExpandedPanel";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getInitials } from "@/util/string";
import { memberName } from "@/util/member";
import {
  formatDuration,
  formatTime,
  getCallIcon,
  getContactDisplayName,
  getUnansweredBadge,
  groupCallsByDate,
  isMissedCall,
  isUnansweredCall,
  isVoicemail,
} from "@/util/call";
import ContactActivitySheet from "@/components/contacts/ContactActivitySheet";
import VConExportButton from "@/components/vcon/VConExportButton";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { apiErrorMessage } from "@/util/apiError";

const HIGHLIGHT_DURATION_MS = 3000;

function formatFilterDate(value: string) {
  return new Date(value + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

const Calls = () => {
  const { data: organisations } = useGetOrganisationsQuery();
  const { setPendingDialNumber, openCallDialog } = useCallDialog();
  const { setPendingRecipient, openMessageDialog } = useMessageDialog();
  const navigate = useNavigate();
  const { callId } = useParams();
  const [searchParams] = useSearchParams();
  const legacyCallId = searchParams.get("call");
  const { data: highlightedCall } = useGetCallQuery(callId ?? "", { skip: !callId });
  const [highlightedCallId, setHighlightedCallId] = useState<string | null>(null);
  const highlightedRowRef = useRef<HTMLDivElement>(null);
  const resolvedCallLinkRef = useRef<string | null>(null);
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  const [expandedCallId, setExpandedCallId] = useState<string | null>(null);
  const [notesFocus, setNotesFocus] = useState<{ callId: string; request: number }>({ callId: "", request: 0 });

  const [page, setPage] = useState(0);
  const [size, setSize] = useState(50);
  const [direction, setDirection] = useState<string>("ALL");
  const [status, setStatus] = useState<string>("ALL");
  const [dispositionId, setDispositionId] = useState<string>("ALL");
  const attentionParam = searchParams.get("attention");
  const [attentionFilter, setAttentionFilter] = useState<
    "missedWithoutCallback" | "noDisposition" | "unreviewedRecording" | null
  >(
    attentionParam === "missedWithoutCallback" ||
      attentionParam === "noDisposition" ||
      attentionParam === "unreviewedRecording"
      ? attentionParam
      : null,
  );
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [restoreCall] = useRestoreCallMutation();
  const debouncedSearch = useDebouncedValue(search.trim(), 300);

  const statsRange = {
    from: fromDate ? new Date(fromDate).toISOString() : undefined,
    to: toDate ? new Date(toDate + "T23:59:59").toISOString() : undefined,
  };
  const { data: dispositions = [] } = useGetCallDispositionsQuery({}, { skip: !organisations?.length });
  const { data: stats } = useGetCallStatsQuery(statsRange, { skip: !organisations?.length });

  const { data, isLoading } = useGetCallsQuery(
    {
      page,
      size,
      direction: direction !== "ALL" ? direction : undefined,
      status: status !== "ALL" ? status : undefined,
      dispositionId: dispositionId !== "ALL" ? dispositionId : undefined,
      hasDisposition: attentionFilter === "noDisposition" ? false : undefined,
      unreviewedRecording: attentionFilter === "unreviewedRecording" ? true : undefined,
      missedWithoutCallback: attentionFilter === "missedWithoutCallback" ? true : undefined,
      archived: showArchived || undefined,
      q: debouncedSearch || undefined,
      from: statsRange.from,
      to: statsRange.to,
    },
    { skip: !organisations?.length },
  );

  const calls = useMemo(() => data?.items ?? [], [data]);

  const dateRangeLabel =
    fromDate && toDate
      ? `${formatFilterDate(fromDate)} to ${formatFilterDate(toDate)}`
      : fromDate
        ? `From ${formatFilterDate(fromDate)}`
        : toDate
          ? `Until ${formatFilterDate(toDate)}`
          : "All dates";

  const handleRowToggle = (call: (typeof calls)[number]) => {
    if (!call.id) {
      return;
    }
    if (expandedCallId === call.id) {
      setExpandedCallId(null);
      return;
    }
    setExpandedCallId(call.id);
  };

  const focusNotes = (call: (typeof calls)[number]) => {
    if (!call.id) {
      return;
    }
    setExpandedCallId(call.id);
    setNotesFocus((current) => ({ callId: call.id ?? "", request: current.request + 1 }));
  };

  const handleRestore = async (call: (typeof calls)[number]) => {
    if (!call.id) {
      return;
    }
    try {
      await restoreCall(call.id).unwrap();
      toast.success("Call restored");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to restore call"));
    }
  };

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [direction, status, dispositionId, attentionFilter, debouncedSearch, size, fromDate, toDate, showArchived]);

  useEffect(() => {
    if (!legacyCallId) {
      return;
    }
    const params = new URLSearchParams(searchParams);
    params.delete("call");
    const query = params.toString();
    navigate(`/calls/${legacyCallId}${query ? `?${query}` : ""}`, { replace: true });
  }, [legacyCallId, searchParams, navigate]);

  useEffect(() => {
    if (!callId) {
      resolvedCallLinkRef.current = null;
      setHighlightedCallId(null);
      return;
    }
    const linkedCall = calls.find((call) => call.id === callId);
    if (resolvedCallLinkRef.current === callId || !linkedCall) {
      return;
    }
    resolvedCallLinkRef.current = callId;
    setHighlightedCallId(callId);
    if (expandedCallId !== callId) {
      handleRowToggle(linkedCall);
    }
  }, [callId, calls, expandedCallId, handleRowToggle]);

  useEffect(() => {
    if (!highlightedCallId) {
      return;
    }
    highlightedRowRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
    const timer = window.setTimeout(() => setHighlightedCallId(null), HIGHLIGHT_DURATION_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [highlightedCallId]);

  if (!organisations || !organisations.length) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <h2 className="text-lg font-semibold mb-2">No organisation</h2>
          <p className="text-muted-foreground mb-4">You need to be part of an organisation to use this feature.</p>
          <Button onClick={() => navigate("/settings/organisations/new")}>Create organisation</Button>
        </div>
      </div>
    );
  }

  const groupedCalls = groupCallsByDate(calls);

  return (
    <div className="flex h-full">
      <div className="flex-1 flex flex-col min-w-0">
        <div className="space-y-4 p-4 border-b border-border">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">{showArchived ? "Archived calls" : "Calls"}</h1>
              <p className="text-muted-foreground">
                {showArchived ? "Archived calls stay here until you restore or delete them" : "View and manage your call history"}
              </p>
            </div>
            <Button
              variant={showArchived ? "secondary" : "outline"}
              size="sm"
              className="h-9 gap-2"
              onClick={() => setShowArchived((value) => !value)}
            >
              <Archive className="h-4 w-4" />
              {showArchived ? "Back to calls" : "Archived"}
            </Button>
          </div>

          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="border rounded-xl px-4 py-3">
                <p className="text-xs font-medium text-muted-foreground">Calls{fromDate || toDate ? "" : " today"}</p>
                <p className="text-2xl font-bold tabular-nums">{stats.calls}</p>
              </div>
              <div className="border rounded-xl px-4 py-3">
                <p className="text-xs font-medium text-muted-foreground">Conversations</p>
                <p className="text-2xl font-bold tabular-nums" style={{ color: "var(--success)" }}>
                  {stats.conversations}
                </p>
              </div>
              <div className="border rounded-xl px-4 py-3">
                <p className="text-xs font-medium text-muted-foreground">Talk time</p>
                <p className="text-2xl font-bold tabular-nums">{formatDuration(stats.talkTimeSeconds)}</p>
              </div>
              <div className="border rounded-xl px-4 py-3">
                <p className="text-xs font-medium text-muted-foreground">Missed</p>
                <p className="text-2xl font-bold tabular-nums">{stats.missed}</p>
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or phone number..."
              className="h-9 flex-1 min-w-44 max-w-xs"
            />

            <Select value={direction} onValueChange={setDirection}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Direction" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All directions</SelectItem>
                <SelectItem value="INBOUND">Inbound</SelectItem>
                <SelectItem value="OUTBOUND">Outbound</SelectItem>
              </SelectContent>
            </Select>

            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All statuses</SelectItem>
                <SelectItem value="COMPLETED">Completed</SelectItem>
                <SelectItem value="MISSED">Missed</SelectItem>
                <SelectItem value="NO_ANSWER">No Answer</SelectItem>
                <SelectItem value="BUSY">Busy</SelectItem>
                <SelectItem value="FAILED">Failed</SelectItem>
                <SelectItem value="CANCELED">Canceled</SelectItem>
              </SelectContent>
            </Select>

            <Select value={dispositionId} onValueChange={setDispositionId}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Disposition" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All dispositions</SelectItem>
                {dispositions.map((d) => (
                  <SelectItem key={d.id} value={d.id!}>
                    <span className="inline-flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: d.color }} />
                      {d.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className={cn(
                    "h-9 gap-2 font-normal",
                    (fromDate || toDate) && "border-primary bg-primary/10 hover:bg-primary/10",
                  )}
                >
                  <CalendarDays className="h-4 w-4" />
                  {dateRangeLabel}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-3" align="start">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Input
                      type="date"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      className="h-9 w-36"
                    />
                    <span className="text-sm text-muted-foreground">to</span>
                    <Input
                      type="date"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      className="h-9 w-36"
                    />
                  </div>
                  {(fromDate || toDate) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full"
                      onClick={() => {
                        setFromDate("");
                        setToDate("");
                      }}
                    >
                      Clear dates
                    </Button>
                  )}
                </div>
              </PopoverContent>
            </Popover>

            {stats && (
              <>
                <div className="flex-1" />
                <TooltipProvider delayDuration={150}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        aria-label="Missed calls without callback"
                        className={cn(
                          "h-9 gap-2 rounded-full",
                          attentionFilter === "missedWithoutCallback" && "border-primary bg-primary/10 hover:bg-primary/10",
                        )}
                        onClick={() =>
                          setAttentionFilter((current) =>
                            current === "missedWithoutCallback" ? null : "missedWithoutCallback",
                          )
                        }
                      >
                        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: "var(--destructive)" }} />
                        <span className="tabular-nums">{stats.missedWithoutCallback}</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Missed · no callback</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        aria-label="Calls without disposition"
                        className={cn(
                          "h-9 gap-2 rounded-full",
                          attentionFilter === "noDisposition" && "border-primary bg-primary/10 hover:bg-primary/10",
                        )}
                        onClick={() => setAttentionFilter((current) => (current === "noDisposition" ? null : "noDisposition"))}
                      >
                        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: "var(--warning)" }} />
                        <span className="tabular-nums">{stats.noDisposition}</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>No disposition</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        aria-label="Unreviewed recordings"
                        className={cn(
                          "h-9 gap-2 rounded-full",
                          attentionFilter === "unreviewedRecording" && "border-primary bg-primary/10 hover:bg-primary/10",
                        )}
                        onClick={() =>
                          setAttentionFilter((current) =>
                            current === "unreviewedRecording" ? null : "unreviewedRecording",
                          )
                        }
                      >
                        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: "var(--info)" }} />
                        <span className="tabular-nums">{stats.unreviewedRecordings}</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Unreviewed recordings</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          {isLoading && (
            <div className="p-6 space-y-4">
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-64 w-full" />
            </div>
          )}

          {!isLoading && calls.length === 0 && (
            <div className="flex items-center justify-center h-64 text-muted-foreground">
              {showArchived ? "No archived calls" : "No calls found"}
            </div>
          )}

          {highlightedCall && (
            <div className="mx-6 mt-4 mb-4 flex items-center gap-3 rounded-lg border border-primary/40 bg-primary/5 px-4 py-3">
              <Phone className="h-4 w-4 shrink-0 text-destructive" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">
                  {getContactDisplayName(highlightedCall.contact) || highlightedCall.phoneNumber}
                </p>
                <p className="text-xs text-muted-foreground">
                  {isMissedCall(highlightedCall.direction, highlightedCall.status) ? "Missed call" : highlightedCall.status}
                  {highlightedCall.createdAt
                    ? ` at ${new Date(highlightedCall.createdAt).toLocaleString()}`
                    : ""}
                </p>
                {highlightedCall.id && highlightedCall.callFlow && (
                  <div className="mt-2">
                    <CallFlowStepsList callId={highlightedCall.id} />
                  </div>
                )}
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setPendingDialNumber(highlightedCall.phoneNumber);
                  openCallDialog();
                }}
              >
                <Phone className="mr-1 h-4 w-4" />
                Call back
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  const query = searchParams.toString();
                  navigate(`/calls${query ? `?${query}` : ""}`, { replace: true });
                }}
              >
                Dismiss
              </Button>
            </div>
          )}

          {!isLoading &&
            calls.length > 0 &&
            Array.from(groupedCalls.entries()).map(([date, dateCalls]) => (
              <div key={date}>
                <div className="sticky top-0 bg-muted px-6 py-2 text-sm font-medium text-muted-foreground">{date}</div>
                <div className="divide-y divide-border">
                  {dateCalls.map((call) => (
                    <div
                      key={call.id}
                      ref={call.id === highlightedCallId ? highlightedRowRef : null}
                      className={`flex items-center gap-3 px-6 py-3 hover:bg-accent/50 transition-colors cursor-pointer ${
                        selectedContactId && call.contact?.id === selectedContactId ? "bg-accent" : ""
                      } ${call.id === highlightedCallId ? "bg-primary/10" : ""}`}
                      onClick={() => handleRowToggle(call)}
                    >
                      <Avatar
                        className="h-10 w-10"
                        onClick={(e) => {
                          if (call.contact) {
                            e.stopPropagation();
                            setSelectedContactId(call.contact.id === selectedContactId ? null : call.contact.id);
                          }
                        }}
                      >
                        {call.contact?.photo && (
                          <AvatarImage
                            src={call.contact.photo}
                            alt={getContactDisplayName(call.contact) ?? undefined}
                          />
                        )}
                        <AvatarFallback className="bg-muted">
                          {(() => {
                            if (call.contact) {
                              const initials = getInitials(call.contact.firstName, call.contact.lastName);
                              if (initials !== "?") return initials;
                            }
                            return <User className="h-4 w-4" />;
                          })()}
                        </AvatarFallback>
                      </Avatar>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span
                            className={`font-medium truncate ${call.contact ? "hover:underline" : ""}`}
                            onClick={(e) => {
                              if (call.contact) {
                                e.stopPropagation();
                                setSelectedContactId(call.contact.id === selectedContactId ? null : call.contact.id);
                              }
                            }}
                          >
                            {getContactDisplayName(call.contact) ?? call.phoneNumber}
                          </span>
                          {getUnansweredBadge(call.direction, call.status)}
                          {isVoicemail(call) && (
                            <Badge variant="secondary" className="text-xs">
                              Voicemail
                            </Badge>
                          )}
                          {call.recording && !call.recording.reviewedAt && (
                            <Badge className="text-xs">New</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          {getCallIcon(call.direction, call.status)}
                          {getContactDisplayName(call.contact) && <span>{call.phoneNumber}</span>}
                          <span>{formatTime(new Date(call.createdAt!))}</span>
                          {call.duration != null && call.duration > 0 && (
                            <>
                              <span>·</span>
                              <span>{formatDuration(call.duration)}</span>
                            </>
                          )}
                          {call.disposition && (
                            <>
                              <span>·</span>
                              <span className="inline-flex items-center gap-1.5">
                                <span
                                  className="h-2 w-2 rounded-full"
                                  style={{ backgroundColor: call.disposition.color }}
                                />
                                {call.disposition.name}
                              </span>
                            </>
                          )}
                          {call.fromPhoneNumber && (
                            <>
                              <span>·</span>
                              <span className="text-xs">via {call.fromPhoneNumber.alias || call.fromPhoneNumber.number}</span>
                            </>
                          )}
                          {call.handledByMembership?.user && (
                            <>
                              <span>·</span>
                              <span className="text-xs">
                                Handled by {memberName(call.handledByMembership)}
                              </span>
                            </>
                          )}
                        </div>
                        {call.recording && expandedCallId !== call.id && (
                          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                            <span className="inline-flex items-center gap-1">
                              {isVoicemail(call) ? (
                                <Voicemail className="h-3.5 w-3.5" />
                              ) : (
                                <Mic className="h-3.5 w-3.5" />
                              )}
                              {isVoicemail(call) ? "Voicemail received" : "Recorded"}
                            </span>
                            {call.recording.duration != null && call.recording.duration > 0 && (
                              <span>{formatDuration(call.recording.duration)}</span>
                            )}
                          </div>
                        )}
                        {expandedCallId === call.id && (
                          <div className="mt-2">
                            <CallExpandedPanel
                              call={call}
                              focusNotesRequest={notesFocus.callId === call.id ? notesFocus.request : 0}
                            />
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        {showArchived && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 gap-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              void handleRestore(call);
                            }}
                          >
                            <ArchiveRestore className="h-3.5 w-3.5" />
                            Restore
                          </Button>
                        )}
                        {call.id && <VConExportButton resource={{ type: "call", id: call.id }} iconOnly />}
                        {call.recording && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRowToggle(call);
                            }}
                            title="Listen to recording"
                          >
                            <Play className="h-4 w-4" />
                          </Button>
                        )}
                        {isUnansweredCall(call.status) ? (
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPendingDialNumber(call.phoneNumber);
                              openCallDialog();
                            }}
                          >
                            <Phone className="h-3.5 w-3.5 mr-1" />
                            Call back
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPendingDialNumber(call.phoneNumber);
                              openCallDialog();
                            }}
                            title="Call"
                          >
                            <Phone className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPendingRecipient(call.phoneNumber);
                            openMessageDialog();
                          }}
                        >
                          <MessageSquare className="h-4 w-4" />
                        </Button>
                        <CallActionsMenu
                          call={call}
                          onOpen={() => {
                            if (expandedCallId !== call.id) {
                              setExpandedCallId(call.id ?? null);
                            }
                          }}
                          onEditNotes={() => focusNotes(call)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </div>

        {data && data.totalElements > 0 && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-border">
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">
                {data.totalElements} {data.totalElements === 1 ? "call" : "calls"}
                {data.totalPages > 1 && ` · Page ${data.page + 1} of ${data.totalPages}`}
              </span>
              <Select value={String(size)} onValueChange={(v) => setSize(Number(v))}>
                <SelectTrigger className="w-25">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {data.totalPages > 1 && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={data.page === 0} onClick={() => setPage((p) => p - 1)}>
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <Button variant="outline" size="sm" disabled={data.last} onClick={() => setPage((p) => p + 1)}>
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      <ContactActivitySheet
        contactId={selectedContactId}
        onOpenChange={(open) => { if (!open) { setSelectedContactId(null); } }}
      />
    </div>
  );
};

export default Calls;
